import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { Medal, TrendingUp, Lock } from "lucide-react";
import { useSection, useSeo } from "@/contexts/SiteContentContext";

const PROVINCES = ["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"];
const POSITIONS = [
  { v: "P", l: "All Pitchers" }, { v: "C", l: "Catchers" }, { v: "IF", l: "All Infielders" }, { v: "OF", l: "All Outfielders" },
  ...["RHP", "LHP", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "UTIL", "DH"].map((p) => ({ v: p, l: p })),
];
const GRAD_YEARS = [2025, 2026, 2027, 2028, 2029, 2030];

const SCOPES = [
  { key: "national", label: "National" },
  { key: "provincial", label: "Provincial" },
  { key: "position", label: "Position" },
  { key: "graduation_year", label: "Graduation Year" },
];
const SCOPE_KEYS = SCOPES.map((s) => s.key);

export default function Rankings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialScope = SCOPE_KEYS.includes(searchParams.get("scope")) ? searchParams.get("scope") : "national";
  const [scope, setScopeState] = useState(initialScope);
  const [province, setProvince] = useState("ON");
  const [position, setPosition] = useState("P");
  const [gradYear, setGradYear] = useState(2027);
  const [rows, setRows] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null);

  const setScope = (s) => { setScopeState(s); setSearchParams(s === "national" ? {} : { scope: s }, { replace: true }); };
  const c = useSection("rankings");
  useSeo(c.seo_title, c.meta_description);

  useEffect(() => {
    const sp = SCOPE_KEYS.includes(searchParams.get("scope")) ? searchParams.get("scope") : "national";
    setScopeState(sp);
  }, [searchParams]);

  useEffect(() => {
    api.get("/subscription/config").then(({ data }) => setGate(data)).catch(() => setGate(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (scope === "provincial") params.province = province;
    if (scope === "position") params.primary_position = position;
    if (scope === "graduation_year") params.graduation_year = gradYear;

    api
      .get("/rankings", { params })
      .then(({ data }) => {
        setHidden(data.public_visible === false);
        setRows(data.rankings || []);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [scope, province, position, gradYear]);

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="label-overline text-[#E63946]">{c.overline || "CBR Overall Rating"}</div>
          <h1 data-testid="rankings-title" className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mt-2 leading-[0.95]">
            {c.title || "Prospect Rankings"}
          </h1>
          <p className="text-sm text-white/70 mt-4 max-w-2xl">
            {c.subtitle || "Driven by the CBR Overall Rating — a 20-80 blend of verified performance metrics and CBR evaluator scouting grades."}
          </p>
        </div>
      </section>

      {/* Scope tabs */}
      <section className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex flex-wrap gap-2 items-center">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              data-testid={`rankings-scope-${s.key}`}
              onClick={() => setScope(s.key)}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm ${
                scope === s.key ? "bg-[#E63946] text-white" : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"
              }`}
            >
              {s.label}
            </button>
          ))}
          <div className="ml-auto">
            {scope === "provincial" && (
              <select data-testid="rankings-province-select" value={province} onChange={(e) => setProvince(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            {scope === "position" && (
              <select data-testid="rankings-position-select" value={position} onChange={(e) => setPosition(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
                {POSITIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            )}
            {scope === "graduation_year" && (
              <select data-testid="rankings-gradyear-select" value={gradYear} onChange={(e) => setGradYear(Number(e.target.value))} className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
                {GRAD_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-8 min-h-[500px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {gate && gate.leaderboards_gated && !gate.has_leaderboard_access ? (
            <div data-testid="rankings-subscription-gate" className="bg-white border border-slate-200 p-12 text-center max-w-xl mx-auto">
              <Lock className="w-12 h-12 mx-auto text-[#E63946]" />
              <div className="font-display text-2xl uppercase mt-4">Members Only</div>
              <p className="text-sm text-slate-600 mt-2">{gate.upsell_message || "Full rankings are available to CBR Prospects members."}</p>
              <Link to="/membership" className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D90429] text-white px-6 py-3 mt-6 text-sm font-semibold uppercase tracking-wider rounded-sm">Become a Member</Link>
            </div>
          ) : hidden ? (
            <div className="bg-white border border-slate-200 p-12 text-center">
              <TrendingUp className="w-12 h-12 mx-auto text-slate-300" />
              <div className="font-display text-2xl uppercase mt-4">Rankings Coming Soon</div>
              <p className="text-sm text-slate-600 mt-2">CBR rankings are being finalized and will be published shortly.</p>
            </div>
          ) : loading ? (
            <div className="p-10 label-overline text-slate-500">Loading rankings…</div>
          ) : rows.length === 0 ? (
            <div className="bg-white border border-slate-200 p-12 text-center">
              <Medal className="w-12 h-12 mx-auto text-slate-300" />
              <div className="font-display text-2xl uppercase mt-4">No Ranked Prospects</div>
              <p className="text-sm text-slate-600 mt-2">No rated prospects match this scope yet.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 label-overline w-12">Rank</th>
                    <th className="text-left p-3 label-overline">Player</th>
                    <th className="text-left p-3 label-overline">Class</th>
                    <th className="text-left p-3 label-overline">Pos</th>
                    <th className="text-left p-3 label-overline">Province</th>
                    <th className="text-left p-3 label-overline">CBR Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} data-testid={`ranking-row-${r.id}`} className={`border-b border-slate-100 last:border-0 ${i < 3 ? "bg-amber-50/40" : ""}`}>
                      <td className="p-3 font-data text-lg">
                        {i === 0 ? <span className="text-[#E63946]">#1</span> : i === 1 ? <span className="text-[#0A192F]">#2</span> : i === 2 ? <span className="text-amber-700">#3</span> : <span className="text-slate-500">#{r.rank}</span>}
                      </td>
                      <td className="p-3">
                        <Link to={`/prospects/${r.slug}`} className="font-semibold hover:text-[#E63946]">
                          {r.first_name} {r.last_name}
                        </Link>
                        {r.current_team && <div className="text-[11px] text-slate-500">{r.current_team}</div>}
                      </td>
                      <td className="p-3 font-data">{r.graduation_year || "—"}</td>
                      <td className="p-3 font-data">{r.primary_position || "—"}</td>
                      <td className="p-3">{r.province || "—"}</td>
                      <td className="p-3 font-data text-2xl text-[#0A192F]">{Number(r.cbr_overall_rating).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
